import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApartmentsListApi } from '@/features/apartments/hooks/useApartmentsListApi';
import ApartmentsDetails from '@/features/apartments/components/ApartmentsDetails/ApartmentsDetails';
import ApartmentsPageEmpty from '@/features/apartments/components/ApartmentsPageEmpty';
import ApartmentsCardSkeleton from '@/features/apartments/components/ApartmentsCard/ApartmentsCardSkeleton';
import ApartmentsCardMobile from '@/features/apartments/components/ApartmentsCard/ApartmentsCardMobile';
import ApartmentsCard from '@/features/apartments/components/ApartmentsCard/ApartmentsCard';
import { cn } from '@/lib/utils';

type Props = {
	id: string;
	isMobile?: boolean;
};

const OwnerDetailsListApartments: FC<Props> = ({ id, isMobile }) => {
	const [apartmentId, setApartmentId] = useState('');
	const [showApartmentsDetails, setShowApartmentsDetails] = useState(false);

	const { t } = useTranslation();

	const loaderRef = useRef<HTMLDivElement | null>(null);

	const {
		apartmentsListData,
		isLoadingApartmentsListData,
		fetchApartmentsListDataNextPage,
		hasApartmentsListDataNextPage,
		isFetchingApartmentsListDataNextPage,
	} = useApartmentsListApi({ ownerId: id });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasApartmentsListDataNextPage ||
			isFetchingApartmentsListDataNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchApartmentsListDataNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '500px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchApartmentsListDataNextPage,
		hasApartmentsListDataNextPage,
		isFetchingApartmentsListDataNextPage,
	]);

	const handleShowDetails = (id: string) => {
		setApartmentId(id);
		setShowApartmentsDetails(true);
	};

	return (
		<div className='h-full flex flex-col gap-4 select-none'>
			<div className='flex items-center justify-between gap-2'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t('main.owners.components.details.list.apartments.title')}
				</h3>
			</div>
			{!!isLoadingApartmentsListData && (
				<div className='h-full grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]'>
					{Array.from({ length: 20 }).map((_, index) => (
						<ApartmentsCardSkeleton key={index} />
					))}
				</div>
			)}
			{!isLoadingApartmentsListData && !!apartmentsListData?.length && (
				<div className='h-full grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]'>
					{apartmentsListData.map((apartment) =>
						isMobile ? (
							<ApartmentsCardMobile
								key={apartment.id}
								{...apartment}
								showDetails={handleShowDetails}
							/>
						) : (
							<ApartmentsCard
								key={apartment.id}
								{...apartment}
								showDetails={handleShowDetails}
							/>
						),
					)}
				</div>
			)}
			{!isLoadingApartmentsListData && !apartmentsListData?.length && (
				<ApartmentsPageEmpty />
			)}
			<div
				ref={loaderRef}
				className='h-px'
			/>
			{showApartmentsDetails && (
				<ApartmentsDetails
					id={apartmentId}
					onClose={() => setShowApartmentsDetails(false)}
				/>
			)}
		</div>
	);
};

export default OwnerDetailsListApartments;
